/**
 * OperatorCatalogIssuesQueue — v0.9.3 (Bill 2026-05-15).
 *
 * SysOps queue of data-integrity issues raised by the Core Operator
 * Catalog sync. Sources of truth per Bill:
 *   - Entra      → DisplayName, JobTitle, EmailAddress
 *   - SharePoint → Operator Initials, Operator Number
 *   - Derived    → Network ID = email-prefix.toUpperCase()
 *
 * Four issue kinds:
 *   - DuplicateInitials  → fix in SharePoint Core Operator Numbers
 *   - NameMismatch       → SP Operator Name should match Entra DisplayName
 *   - TitleMismatch      → SP Operator Title should match Entra JobTitle
 *   - NetworkIdMismatch  → SP Network ID should match derived value
 *
 * Issues auto-resolve on next sync once the source is corrected.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ShieldCheck, ExternalLink, RefreshCw } from 'lucide-react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { CatalogIssuesTeamsMessage } from './CatalogIssuesTeamsMessage';
import type { OperatorCatalogIssueRow } from '@retail-ops/api-client';

const KIND_LABELS: Record<string, string> = {
  DuplicateInitials: 'Duplicate Initials',
  NameMismatch: 'Name Mismatch',
  TitleMismatch: 'Title Mismatch',
  NetworkIdMismatch: 'Network ID Mismatch',
};

const KIND_TONES: Record<string, 'warn' | 'err' | 'info'> = {
  DuplicateInitials: 'err',
  NameMismatch: 'warn',
  TitleMismatch: 'warn',
  NetworkIdMismatch: 'info',
};

export function OperatorCatalogIssuesQueue(): JSX.Element {
  const api = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<OperatorCatalogIssueRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState<string>('all');
  const [composing, setComposing] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  // Refresh (Bill 2026-10-06: "No way to refresh"): re-reads the SharePoint list, re-runs the checks
  // (issues no longer detected close themselves), then reloads this queue.
  const [refreshing, setRefreshing] = useState(false);
  const refreshCatalog = async (): Promise<void> => {
    setRefreshing(true);
    setError(null);
    try {
      const resp = await api.retail.admin.refreshCoreOperatorCatalog();
      if (!resp.success) setError(`Catalog refresh failed: ${resp.error ?? resp.code ?? 'unknown'}`);
      else {
        const d = (resp.data ?? {}) as Record<string, unknown>;
        setSavedToast(`Catalog refreshed: ${d.cached ?? '?'} operators; ${d.issuesRaised ?? 0} issue(s) raised, ${d.issuesAutoResolved ?? 0} closed.`);
      }
    } catch (e) {
      setError(`Catalog refresh failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRefreshing(false);
      void reloadRef.current?.();
    }
  };
  const reloadRef = useRef<(() => Promise<void>) | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await api.retail.admin.listOperatorCatalogIssues();
      if (resp.success && Array.isArray(resp.data)) setRows(resp.data);
      else setError('Failed to load queue.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);
  reloadRef.current = reload;

  useEffect(() => { void reload(); }, [reload]);

  const onResolve = useCallback(async (issue: OperatorCatalogIssueRow) => {
    setResolvingId(issue.issueId);
    try {
      const resp = await api.retail.admin.resolveOperatorCatalogIssue(issue.issueId);
      if (resp.success) {
        setSavedToast(`Resolved ${KIND_LABELS[issue.kind] ?? issue.kind} for ${issue.operatorInitials ?? '(no initials)'}.`);
        await reload();
      } else {
        setError(resp.error ?? `Resolve failed (${resp.code ?? 'unknown'}).`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setResolvingId(null);
    }
  }, [api, reload]);

  const visible = useMemo(() => {
    if (!rows) return null;
    return filterKind === 'all' ? rows : rows.filter((r) => r.kind === filterKind);
  }, [rows, filterKind]);

  const countsByKind = useMemo(() => {
    const c: Record<string, number> = { all: 0 };
    if (rows) for (const r of rows) {
      c.all = (c.all ?? 0) + 1;
      c[r.kind] = (c[r.kind] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  return (
    <>
      <h1>Operator Catalog Issues queue</h1>
      <p className="page-intro">
        Data-integrity issues between the SharePoint <i>Core Operator Numbers</i> list
        and the authoritative sources (Entra for DisplayName / JobTitle, derived from
        email for Network ID). Each issue tells you where to fix it. After you fix the
        source, press <b>Refresh catalog</b> — issues that no longer apply close themselves. Entra
        governs name, title and e-mail: Core Director must match it exactly.
      </p>
      <div style={{ margin: '8px 0 12px' }}>
        <button type="button" className="btn btn--ghost" disabled={refreshing} onClick={() => void refreshCatalog()}>
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} />{' '}
          {refreshing ? 'Refreshing…' : 'Refresh catalog from SharePoint'}
        </button>{' '}
        <button type="button" className="btn btn--primary" disabled={!rows || rows.length === 0} onClick={() => setComposing(true)}>
          Teams message for SysOps
        </button>
        {composing && rows && <CatalogIssuesTeamsMessage issues={rows} onClose={() => setComposing(false)} />}
      </div>

      {savedToast && (
        <div className="banner banner--ok" role="status">
          <ShieldCheck size={16} className="banner__icon" /> {savedToast}
        </div>
      )}
      {error && <div className="banner banner--err" role="status">{error}</div>}

      <div className="catalog-issues-filter">
        <span className="cell-dim">Filter:</span>
        {(['all', 'DuplicateInitials', 'NameMismatch', 'TitleMismatch', 'NetworkIdMismatch']).map((k) => {
          const c = countsByKind[k] ?? 0;
          if (k !== 'all' && c === 0) return null;
          const active = filterKind === k;
          const label = k === 'all' ? 'All' : (KIND_LABELS[k] ?? k);
          return (
            <button
              key={k}
              type="button"
              className={`pill pill--${active ? 'info' : 'muted'} pill--clickable${active ? ' pill--active' : ''}`}
              onClick={() => setFilterKind(k)}
            >
              {label} ({c})
            </button>
          );
        })}
      </div>

      {!rows && !error && (
        <div className="loading-inline">
          <Loader2 className="spin" size={16} /> Loading queue…
        </div>
      )}

      {rows && rows.length === 0 && (
        <div className="empty">
          <ShieldCheck size={32} className="empty__icon" />
          <p>No outstanding issues. Catalog is in sync with all authoritative sources.</p>
        </div>
      )}

      {visible && visible.length > 0 && (
        <table className="data-table data-table--issues">
          <thead>
            <tr>
              <th>Issue</th>
              <th>Operator</th>
              <th>Detail</th>
              <th>First seen</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.issueId}>
                <td>
                  <span className={`pill pill--${KIND_TONES[r.kind] ?? 'muted'} pill--nowrap`}>
                    {KIND_LABELS[r.kind] ?? r.kind}
                  </span>
                </td>
                <td className="cell-operator">
                  {r.operatorInitials && (
                    <code className="cell-mono cell-strong">{r.operatorInitials}</code>
                  )}
                  {r.emailAddress && (
                    <div className="cell-sub-email">{r.emailAddress}</div>
                  )}
                  {!r.operatorInitials && !r.emailAddress && <span className="cell-dim">—</span>}
                </td>
                <td className="cell-detail" title={r.detail ?? ''}>
                  {r.detail ?? <span className="cell-dim">—</span>}
                </td>
                <td className="cell-mono cell-nowrap">
                  {new Date(r.firstSeenAt).toLocaleString(undefined, {
                    year: 'numeric', month: 'numeric', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </td>
                <td className="ta-right">
                  <div className="row-actions">
                    <a
                      className="btn btn--ghost btn--sm"
                      href="https://mypcb.sharepoint.com/sites/SystemOps/Lists/Core%20Operator%20Numbers/AllItems.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open SharePoint Core Operator Numbers list"
                      aria-label="Open SharePoint list"
                    >
                      <ExternalLink size={14} />
                    </a>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => void onResolve(r)}
                      disabled={resolvingId === r.issueId}
                    >
                      {resolvingId === r.issueId ? 'Resolving…' : 'Resolve'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
